import { useState, useCallback, useRef } from 'react';
import AgoraRTC, { ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import type { AgoraRoomLike } from '../../../types';
import { ErrorCallback } from '../../../types';

/**
 * Publishes screen as the only remote video while sharing (camera is unpublished).
 * Agora remote users only have one `videoTrack`; dual camera+screen would overwrite it.
 */
export default function useScreenShareToggle(
  room: AgoraRoomLike | null,
  cameraVideoTrack: ILocalVideoTrack | undefined,
  onError: ErrorCallback,
  setLocalScreenTrack: (t: ILocalVideoTrack | undefined) => void,
  removeLocalScreenTrack: () => void
) {
  const [isSharing, setIsSharing] = useState(false);
  const screenTrackRef = useRef<ILocalVideoTrack | null>(null);
  /** True if we successfully unpublished the camera before publishing screen (must republish on stop). */
  const cameraWasPublishedRef = useRef(false);
  const cameraTrackRef = useRef(cameraVideoTrack);
  cameraTrackRef.current = cameraVideoTrack;

  const shareScreen = useCallback(() => {
    if (!room) return;

    const run = async () => {
      const client = room.client;
      const cam = cameraTrackRef.current;
      cameraWasPublishedRef.current = false;

      if (cam) {
        try {
          await client.unpublish([cam]);
          cameraWasPublishedRef.current = true;
        } catch {
          /* camera was not published (video off) */
        }
      }

      const trackOrTracks = await AgoraRTC.createScreenVideoTrack({ encoderConfig: '720p_1' });
      const track = Array.isArray(trackOrTracks) ? trackOrTracks[0] : trackOrTracks;
      (track as any).trackName = 'screen';
      screenTrackRef.current = track as ILocalVideoTrack;
      setLocalScreenTrack(track as ILocalVideoTrack);
      await client.publish([track as ILocalVideoTrack]);
      setIsSharing(true);
    };

    run().catch(err => {
      if (err?.message?.includes('Permission') || err?.name === 'NotAllowedError') return;
      onError(err);
    });
  }, [room, onError]);

  const stopScreenShare = useCallback(() => {
    const track = screenTrackRef.current;
    if (!room || !track) return;

    const client = room.client;
    const cam = cameraTrackRef.current;

    client
      .unpublish([track])
      .then(() => {
        screenTrackRef.current = null;
        // This will close the track and remove it from localTracks.
        removeLocalScreenTrack();
        setIsSharing(false);
        const shouldRepublish = cameraWasPublishedRef.current;
        cameraWasPublishedRef.current = false;
        if (shouldRepublish && cam) {
          return client.publish([cam]).catch(onError);
        }
        return undefined;
      })
      .catch(onError);
  }, [room, onError, removeLocalScreenTrack]);

  const toggleScreenShare = useCallback(() => {
    if (!room) return;
    if (isSharing) stopScreenShare();
    else shareScreen();
  }, [room, isSharing, shareScreen, stopScreenShare]);

  return [isSharing, toggleScreenShare] as const;
}
